/**
 * "Sequence my day" (3.2): recommendations on request. The engine proposes an
 * order for the day and the product prices both orders on real drive times. The
 * clinician accepts it or does not — the engine is very good at the order of a
 * day, and it is not the owner of the day.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import type { CalendarBlockInput, CommandIndexCombinedDataQuery, VisitResourceGenericInput } from "~/graphql/__generated__/graphql";
import { fetchCommandDay, simpleDriveTime } from "../../data/mutations";
import { addDaysToKey, dateKeyStart, formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { applyDriveUpdates, driveTotalMinutes, proposeSequence, type SequenceProposal, type SequenceStop } from "../../engine/sequenceDay";
import { acceptSequence } from "../../presenter/actions";
import { useDemoState } from "../../store/useDemoStore";
import type { DateKey } from "@caredna-marlin/utils/homeHealthDemo/state";
import { useBranchClock } from "../../components/branchClock";

type CommandShift = CommandIndexCombinedDataQuery["shift"][number];

function stopsFor(data: CommandIndexCombinedDataQuery, providerId: number, confirmedVisitIds: Set<number>): { stops: SequenceStop[]; shift?: CommandShift; blocks: CalendarBlockInput[]; resources: VisitResourceGenericInput[] } {
  const shift = data.shift.find((s) => s.providerId === providerId && s.status !== "canceled");
  const stops: SequenceStop[] = [];
  const resources: VisitResourceGenericInput[] = [];
  for (const vr of data.visitResource) {
    if (vr.providerId !== providerId || !vr.visit || vr.visit.eventStatus === "canceled") continue;
    const delivered = vr.visit.eventStatus === "complete";
    stops.push({
      key: String(vr.id),
      kind: "visit",
      id: vr.id,
      visitId: vr.visitId,
      label: vr.visit.patient ? `${vr.visit.patient.firstName} ${vr.visit.patient.lastName}`.trim() : "Patient",
      startIso: vr.startTime,
      endIso: vr.endTime,
      coordinates: vr.visit.addressCoordinates ?? vr.visit.patient?.addressCoordinates ?? undefined,
      fixed: delivered || confirmedVisitIds.has(vr.visitId),
      reason: delivered ? "already delivered" : confirmedVisitIds.has(vr.visitId) ? "the patient confirmed this window" : undefined,
    });
    resources.push({
      id: vr.id,
      eventKey: String(vr.id),
      providerId: vr.providerId,
      startTime: vr.startTime,
      endTime: vr.endTime,
      startOfShiftDriveTime: vr.startOfShiftDriveTime ?? 0,
      interVisitDriveTime: vr.interVisitDriveTime ?? 0,
      endOfShiftDriveTime: vr.endOfShiftDriveTime ?? 0,
      inHome: true,
      // The resource's own address, which `VisitResourceGenericInput` began
      // requiring when main came down on 2026-09-24. It is what the product
      // passes here (`hooks/useVisitDragDrop.ts`), and the query already
      // selects it, so the demo prices a day off the same field the command
      // center does rather than off the visit's address alone.
      addressCoordinates: vr.addressCoordinates,
      visit: { eventStatus: vr.visit.eventStatus, addressCoordinates: vr.visit.addressCoordinates ?? "" },
    });
  }
  const blocks: CalendarBlockInput[] = [];
  for (const b of data.calendarBlock) {
    if (b.providerId !== providerId || b.eventStatus === "canceled") continue;
    stops.push({ key: `calendarBlock-${b.id}`, kind: "block", id: b.id, label: b.title ?? "Block", startIso: b.startTime, endIso: b.endTime, coordinates: b.addressCoordinates ?? undefined, fixed: true, reason: "a block" });
    blocks.push({
      id: b.id,
      eventKey: `calendarBlock-${b.id}`,
      providerId: b.providerId,
      startTime: b.startTime,
      endTime: b.endTime,
      addressCoordinates: b.addressCoordinates,
      eventStatus: b.eventStatus,
      startOfShiftDriveTime: b.startOfShiftDriveTime ?? 0,
      interVisitDriveTime: b.interVisitDriveTime ?? 0,
      endOfShiftDriveTime: b.endOfShiftDriveTime ?? 0,
    });
  }
  return { stops, shift, blocks, resources };
}

export function SequenceDayDialog({ date, providerId, onClose }: { date: DateKey; providerId: number; onClose: () => void }) {
  const client = useApolloClient();
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [proposal, setProposal] = useState<SequenceProposal>();
  const [drive, setDrive] = useState<{ current?: number; proposed?: number }>({});
  // A stable key: the snapshot hands back a fresh annotations object on every poll,
  // and re-pricing the day costs two real drive-time reads.
  const confirmedKey = useMemo(
    () =>
      Object.values(state.annotations)
        .filter((a) => a.round?.status === "confirmed")
        .map((a) => a.realVisitId)
        .sort((a, b) => a - b)
        .join(","),
    [state.annotations],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await fetchCommandDay(client, {
        filterStartTime: dateKeyStart(date, tz).toISOString(),
        filterEndTime: dateKeyStart(addDaysToKey(date, 1), tz).toISOString(),
      });
      const confirmed = new Set(confirmedKey ? confirmedKey.split(",").map(Number) : []);
      const { stops, shift, blocks, resources } = stopsFor(data, providerId, confirmed);
      const next = proposeSequence(stops, shift?.provider?.addressCoordinates ?? undefined, { shiftEndIso: shift?.endTime ?? undefined });
      setProposal(next);
      if (!shift || next.changes.length === 0) {
        setLoading(false);
        return;
      }
      const shiftInput = {
        providerId,
        startTime: shift.startTime,
        endTime: shift.endTime,
        addressCoordinates: shift.addressCoordinates ?? "",
        useShiftCenterDriveTimes: shift.useShiftCenterDriveTimes,
        shiftCenter: null,
      };
      const byKey = new Map(next.proposed.map((s) => [s.key, s]));
      const proposedResources = resources.map((r) => {
        const stop = byKey.get(String(r.eventKey));
        return stop ? { ...r, startTime: stop.startIso, endTime: stop.endIso } : r;
      });
      const [currentSegments, proposedSegments] = await Promise.all([
        simpleDriveTime(client, { shift: shiftInput, providerAddress: { addressCoordinates: shift.provider?.addressCoordinates ?? "" }, visitResources: resources, calendarBlocks: blocks }),
        simpleDriveTime(client, { shift: shiftInput, providerAddress: { addressCoordinates: shift.provider?.addressCoordinates ?? "" }, visitResources: proposedResources, calendarBlocks: blocks }),
      ]);
      // The reply is a diff over what we sent, so both totals are read off the
      // day with the reply laid on top of it — never off the reply alone.
      setDrive({
        current: driveTotalMinutes(applyDriveUpdates([...resources, ...blocks], currentSegments)),
        proposed: driveTotalMinutes(applyDriveUpdates([...proposedResources, ...blocks], proposedSegments)),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client, date, providerId, confirmedKey, tz]);

  useEffect(() => {
    void load();
  }, [load]);

  const accept = async () => {
    if (!proposal) return;
    setSaving(true);
    try {
      await acceptSequence(proposal.changes);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const clock = useBranchClock(tz);
  const time = (iso: string) => clock.time(iso);
  const delta = drive.current !== undefined && drive.proposed !== undefined ? drive.proposed - drive.current : undefined;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Proposed Schedule for {formatInBranchZone(new Date(`${date}T12:00:00Z`), "UTC", "MMM d")}</DialogTitle>
      <DialogContent>
        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Working out the order and the driving…</Typography>
          </Stack>
        )}
        {error && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {/* Two things the engine will not decide, said plainly rather than
            silently proposed: an overlap between stops that are both fixed, and
            a day that no longer fits inside the shift. Neither blocks Accept. */}
        {!loading && proposal && proposal.fixedConflicts.length > 0 && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            {proposal.fixedConflicts.map((c) => `${c.a} and ${c.b} overlap`).join("; ")} — both are fixed, so the order here leaves them as they are.
          </Alert>
        )}
        {!loading && proposal?.pastShiftEnd && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            {proposal.pastShiftEnd.label} would end at {time(proposal.pastShiftEnd.endIso)}, after the shift ends at {time(proposal.pastShiftEnd.shiftEndIso)}.
          </Alert>
        )}
        {!loading && proposal && proposal.changes.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {proposal.fixedConflicts.length > 0 ? "There is nothing the engine can move here." : "The day is already in the order the engine would choose, with nothing overlapping."}
          </Typography>
        )}
        {!loading && proposal && proposal.changes.length > 0 && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={4}>
              <Column title="Now" stops={proposal.current} time={time} total={drive.current} />
              <Divider orientation="vertical" flexItem />
              <Column title="Proposed" stops={proposal.proposed} time={time} total={drive.proposed} />
            </Stack>
            {delta !== undefined && (
              <Typography variant="body2" color={delta < 0 ? "success.main" : "text.secondary"}>
                {delta < 0 ? `${Math.abs(delta)} minutes less driving` : delta === 0 ? "Same driving" : `${delta} minutes more driving`}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={loading || saving || !proposal || proposal.changes.length === 0} onClick={accept}>
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Column({ title, stops, time, total }: { title: string; stops: SequenceStop[]; time: (iso: string) => string; total?: number }) {
  return (
    <Stack spacing={0.5} sx={{ flex: 1 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
      {stops.map((s) => (
        <Typography key={`${title}-${s.key}`} variant="body2" color={s.fixed ? "text.secondary" : "text.primary"}>
          {time(s.startIso)} · {s.label}
          {s.fixed ? " (stays)" : ""}
        </Typography>
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
        {total === undefined ? "Driving —" : `Driving ${total} min`}
      </Typography>
    </Stack>
  );
}
